import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { ReferralBlock } from "./ReferralBlock";
import { api, C } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AVATAR_CATEGORIES, avatarSrc } from "../lib/avatars";
import { JoinButton } from "./JoinDialog";
import { Switch } from "./ui/switch";

// Screen name + avatar picker (6 categories × 10). Saved via PATCH /api/profile; the server rejects profanity
// spelled forwards or backwards. Takes effect at the table the next time you sit down.
export const ProfileSection = () => {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.display_name || "");
  const [cat, setCat] = useState(AVATAR_CATEGORIES[0].id);
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState(null); // live check: {available, reason} for the typed name
  const changed = name.trim() && name.trim() !== user?.display_name;
  useEffect(() => {
    if (!changed) { setAvail(null); return; }
    const id = setTimeout(() => api.get(C.ApiRoutes.NAME_AVAILABLE, { params: { name: name.trim() } }).then((r) => setAvail(r.data)).catch(() => setAvail(null)), 350);
    return () => clearTimeout(id);
  }, [name, changed]);
  const save = async (patch) => {
    setBusy(true);
    try {
      await api.patch(C.ApiRoutes.PROFILE, patch);
      await refresh();
      if ("event_emails" in patch) toast.success(patch.event_emails ? "Event emails on" : "Event emails off");
      else toast.success(patch.avatar ? "Avatar updated" : "Screen name updated", { description: "Shows at the table next time you sit down." });
    } catch (e) {
      const d = e.response?.data?.detail;
      const title = d?.code === C.ErrorCode.NAME_NOT_ALLOWED ? "That name isn't allowed" : d?.code === C.ErrorCode.NAME_TAKEN ? "That name is taken" : "Couldn't save";
      toast.error(title, { description: typeof d === "object" ? d.detail : undefined });
    } finally { setBusy(false); }
  };
  const guest = !!user?.guest;
  const cats = guest ? AVATAR_CATEGORIES.filter((c) => c.id === "men" || c.id === "women") : AVATAR_CATEGORIES; // guests: faces only
  const items = cats.find((c) => c.id === cat)?.items || [];
  return (
    <section data-testid="profile-section">
      <h3 className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3 flex items-center gap-3">Profile
        <span data-testid="profile-tag" title="Your permanent player tag — it never changes, even if you rename" className="font-mono text-[11px] tracking-normal normal-case text-amber-300/90">{user?.tag}</span>
      </h3>
      <div className="flex items-center gap-3 mb-4">
        <img src={avatarSrc(user?.avatar)}
          alt="" className="w-14 h-14 rounded-full bg-slate-800 border-2 border-amber-400/60" data-testid="profile-avatar" />
        <div className="flex-1">
          <label className="text-[11px] text-slate-400">Screen name</label>
          <div className="flex gap-2 mt-1">
            <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={24} disabled={guest}
              className={`flex-1 bg-black/40 border rounded-md px-2 py-1.5 text-sm text-amber-50 ${avail ? (avail.available ? "border-emerald-400/60" : "border-red-400/60") : "border-white/10"}`} />
            <button data-testid="profile-name-save" disabled={busy || !changed || (avail && !avail.available)} onClick={() => save({ display_name: name })}
              className="btn-gold rounded-md px-3 text-xs font-display disabled:opacity-40">Save</button>
          </div>
          {guest && <div data-testid="profile-guest-note" className="text-[11px] mt-1 text-slate-400">Join to pick your own name.</div>}
          {avail && <div data-testid="profile-name-availability" className={`text-[11px] mt-1 ${avail.available ? "text-emerald-300" : "text-red-300"}`}>{avail.available ? "Available" : avail.reason}</div>}
        </div>
      </div>
      <div className="flex gap-1 mb-3 flex-wrap">
        {cats.map((c) => (
          <button key={c.id} data-testid={`avatar-cat-${c.id}`} onClick={() => setCat(c.id)}
            className={`text-[11px] px-2.5 py-1 rounded-full ${cat === c.id ? "bg-amber-500 text-slate-900" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{c.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-2" data-testid="avatar-grid">
        {items.map((a) => {
          const active = user?.avatar === a.id;
          return (
            <button key={a.id} data-testid={`avatar-${a.id}`} title={a.label} disabled={busy} onClick={() => save({ avatar: a.id })}
              className={`relative rounded-full overflow-hidden aspect-square border-2 transition-transform hover:scale-105 ${active ? "border-amber-400" : "border-white/10 hover:border-white/40"}`}>
              <img src={a.url} alt={a.label} loading="lazy" className="w-full h-full object-cover bg-slate-800" />
              {active && <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Check className="w-5 h-5 text-amber-300" /></span>}
            </button>
          );
        })}
      </div>
      {guest ? (
        <div data-testid="profile-guest-join" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 flex items-center gap-3">
          <p className="flex-1 text-[11px] text-slate-300">You're playing as a guest. Join free for <span className="text-amber-300">50,000 chips</span>, every avatar, table colours, card decks, VIP points, club rooms, leaderboards and monthly prizes.</p>
          <JoinButton testId="profile-join-button" />
        </div>
      ) : (<>
        <label className="mt-4 flex items-center justify-between gap-3 text-sm text-amber-50" data-testid="profile-event-emails">
          <span>Tournament &amp; event emails<span className="block text-[11px] text-slate-400">A weekly email of what's coming up</span></span>
          <Switch data-testid="profile-event-emails-toggle" checked={user?.event_emails !== false} onCheckedChange={(c) => save({ event_emails: c })} />
        </label>
        <div className="mt-4"><ReferralBlock /></div>
      </>)}
    </section>
  );
};
